import { useEffect, useRef, useState } from 'react';

interface EditFormBaseline {
  userId: number;
  userDetail: unknown;
  key: string;
}

interface UseEditUserDirtyStateParams {
  userId: number;
  userDetail: unknown;
  currentEditKey: string;
  isHydrated: boolean;
  shouldResetBaseline?: boolean;
}

interface SaveBarVisibilityParams {
  isExistingUser: boolean;
  isOrderManagementAdmin: boolean;
  isEditDirty: boolean;
  isSavedNewUser: boolean;
  isSavedNewUserDirty: boolean;
}

export const shouldShowUserSaveBar = ({
  isExistingUser,
  isOrderManagementAdmin,
  isEditDirty,
  isSavedNewUser,
  isSavedNewUserDirty,
}: SaveBarVisibilityParams): boolean => {
  if (isExistingUser) {
    return isOrderManagementAdmin || isEditDirty;
  }

  return !isSavedNewUser || isSavedNewUserDirty;
};

export const useEditUserDirtyState = ({
  userId,
  userDetail,
  currentEditKey,
  isHydrated,
  shouldResetBaseline = false,
}: UseEditUserDirtyStateParams): boolean => {
  const [baseline, setBaseline] = useState<EditFormBaseline | null>(null);
  const previouslyResetBaselineRef = useRef(false);

  useEffect(() => {
    setBaseline(null);
  }, [userDetail, userId]);

  useEffect(() => {
    if (userId <= 0 || !isHydrated) {
      return;
    }

    setBaseline((currentBaseline) => {
      if (currentBaseline?.userId === userId && currentBaseline.userDetail === userDetail) {
        return currentBaseline;
      }

      return {
        userId,
        userDetail,
        key: currentEditKey,
      };
    });
  }, [currentEditKey, isHydrated, userDetail, userId]);

  useEffect(() => {
    if (shouldResetBaseline && !previouslyResetBaselineRef.current && userId > 0 && isHydrated) {
      setBaseline({
        userId,
        userDetail,
        key: currentEditKey,
      });
    }

    previouslyResetBaselineRef.current = shouldResetBaseline;
  }, [currentEditKey, isHydrated, shouldResetBaseline, userDetail, userId]);

  return Boolean(
    userId > 0 &&
      isHydrated &&
      baseline?.userId === userId &&
      baseline.userDetail === userDetail &&
      baseline.key !== currentEditKey,
  );
};
